import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { closeExternalBrowser, getAppRouteFromUrl, isNativeApp } from '../lib/appPlatform';
import { onPushOpened } from '../lib/push';
import { rememberShotJob } from '../lib/aiShotJob';
import { supabase } from '../lib/supabase';
import { useLocale } from '../context/LocaleContext';
import { showToast } from './ui/Toast';
import {
  RESET_PASSWORD_PATH,
  describeAuthRedirectError,
  isRecoveryLink,
  noteRecoveryLink,
  parseAuthRedirectError,
} from '../lib/authLink';

export default function NativeAppBridge() {
  const navigate = useNavigate();
  const { t } = useLocale();

  React.useEffect(() => {
    if (!isNativeApp()) return;

    let cancelled = false;
    let listener: { remove: () => Promise<void> } | null = null;

    // Establish the Supabase session straight from the raw deep-link URL.
    // OAuth returns tokens in the URL hash (#access_token=...&refresh_token=...)
    // or a ?code=... for PKCE. We parse the raw string here rather than routing
    // through AuthCallback, because React Router navigation drops the hash.
    const handleAuthFromUrl = async (rawUrl: string): Promise<boolean> => {
      let u: URL;
      try { u = new URL(rawUrl); } catch { return false; }

      const hash = u.hash.startsWith('#') ? u.hash.slice(1) : u.hash;
      const hp = new URLSearchParams(hash);
      const access_token = hp.get('access_token');
      const refresh_token = hp.get('refresh_token');
      const code = u.searchParams.get('code');

      try {
        if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({ access_token, refresh_token });
          if (error) throw error;
          console.log('[auth] session set from deep-link tokens');
          return true;
        }
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(rawUrl);
          if (error) throw error;
          console.log('[auth] session set from deep-link code');
          return true;
        }
      } catch (e: any) {
        console.error('[auth] deep-link session error:', e?.message || e);
      }
      return false;
    };

    const handleUrl = async (url?: string | null) => {
      if (!url || cancelled) return;
      console.log('[auth] deep link received:', url.split('#')[0]);

      const looksLikeAuth =
        url.includes('auth/callback') || url.includes('access_token=') || url.includes('code=');

      if (looksLikeAuth) {
        noteRecoveryLink(url);
        const ok = await handleAuthFromUrl(url);
        await closeExternalBrowser();
        if (cancelled) return;
        if (ok) {
          // Notify the shell (closes the auth modal, refreshes profile/data),
          // mirroring the email/password sign-in path.
          window.dispatchEvent(new Event('auth:changed'));
          // A reset link only signs the user in — the password itself is chosen
          // on the reset screen.
          navigate(isRecoveryLink(url) ? RESET_PASSWORD_PATH : '/', { replace: true });
          return;
        }
        // Otherwise the browser just closes on the user with nothing to show for
        // it, so say what went wrong and put the sign-in sheet back up.
        const redirectError = parseAuthRedirectError(url);
        showToast(
          redirectError
            ? describeAuthRedirectError(redirectError, t)
            : t('Sign-in failed', 'Sign-in failed')
        );
        window.dispatchEvent(new Event('auth:open'));
        return;
      }

      const nextRoute = getAppRouteFromUrl(url);
      if (!nextRoute) return;
      await closeExternalBrowser();
      navigate(nextRoute, { replace: nextRoute.startsWith('/auth/callback') });
    };

    const setup = async () => {
      const { App } = await import('@capacitor/app');

      const launchUrl = await App.getLaunchUrl();
      await handleUrl(launchUrl?.url);
      listener = await App.addListener('appUrlOpen', ({ url }) => {
        void handleUrl(url);
      });
    };

    void setup();

    return () => {
      cancelled = true;
      if (listener) {
        void listener.remove();
      }
    };
  }, [navigate, t]);

  // A tapped notification has to land on the thing it was about, not on the home
  // screen. Everything needed to get there travels in the payload, so this works
  // even from a cold launch where the app has no idea what happened while it was
  // gone — a screenshot read that finished, or an exercise request either way.
  React.useEffect(() => {
    if (!isNativeApp()) return;
    let detach: (() => void) | null = null;
    let cancelled = false;

    void onPushOpened((data) => {
      // An exercise request, either end of it: the admin tapping "X requested"
      // and the requester tapping "X is now in the catalog" both want the
      // exercise list — that is where the review queue sits and where a newly
      // added row shows up.
      if (
        data?.type === 'exercise-request' ||
        data?.type === 'exercise-approved' ||
        data?.type === 'exercise-rejected'
      ) {
        window.dispatchEvent(new CustomEvent('app:select-tab', { detail: 'exercises' }));
        // The card may already be mounted from an earlier visit, holding the
        // catalog as it was before the approval.
        window.dispatchEvent(new Event('exercises:refresh'));
        return;
      }

      if (data?.type !== 'ai-screenshot-ready' || !data.jobId) return;
      // The modal resumes from storage, so writing the id here is what makes a
      // cold launch behave the same as a resume.
      rememberShotJob(data.jobId);
      window.dispatchEvent(new CustomEvent('app:select-tab', { detail: 'create' }));
      window.dispatchEvent(new Event('ai-planner:open'));
    }).then((off) => {
      if (cancelled) off();
      else detach = off;
    });

    return () => {
      cancelled = true;
      if (detach) detach();
    };
  }, []);

  return null;
}
